import { Body, Checkbox, H2, SubBody, TextField } from '@rikstv/shared-components';
import { useEffect, useMemo } from 'react';
import Card from '~/components/shared/layout/Card';
import { UserDataInformation } from '../../campaign/checkout/user-data-information-box/UserDataInformation';
import { BasketFields, useBasket } from '../../campaign/useBasket';
import { VerificationStep } from '../../campaign/verification/Verification';
import { PROVIDER_SELECTION_ERROR_ID, VerificationProviderSelection } from '../../campaign/verification/VerificationProviderSelection';

import { addDays } from 'date-fns/addDays';
import { format } from 'date-fns/format';
import { useSearchParams } from 'react-router-dom';
import ErrorLabel from '~/components/shared/error-label/ErrorLabel';
import Icon from '~/components/shared/icons/Icon';
import { type CustomerInfoFields, getFormFieldProps } from '../../campaign/form.utils';
import styles from './VerificationAndCustomerInfo.module.css';

export const VerificationAndCustomerInfo = () => {
  const verificationResponse = useBasket((s) => s.verificationResponse);
  const verificationProvider = useBasket((s) => s.selectedVerificationProvider);

  const resetVerification = useBasket((s) => s.resetVerification);
  const [searchParams] = useSearchParams();
  const restartRequired = searchParams.get('restartRequired');

  useEffect(() => {
    if (restartRequired != null) {
      resetVerification();
    }
  }, [restartRequired, resetVerification]);

  if (!verificationResponse) {
    return <Verification />;
  }

  return <ContactInfo verificationResponse={verificationResponse} verificationProvider={verificationProvider} />;
};

const Verification = () => {
  const stepError = useBasket((s) => s.stepError);
  const setStepError = useBasket((s) => s.setStepError);
  const selectedProvider = useBasket((s) => s.selectedVerificationProvider);
  const setVerificationProvider = useBasket((s) => s.setVerificationProvider);
  const onChange: React.ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    setVerificationProvider(target.value);
    setStepError(undefined);
  };
  // reset on refresh page
  useEffect(() => setVerificationProvider(''), [setVerificationProvider]);

  // Handled in StepNavigation
  const showMustSelectProvider = false;

  return (
    <VerificationStep showMustSelectProvider={showMustSelectProvider} providerName={selectedProvider}>
      <VerificationProviderSelection handleProviderChange={onChange} selectedProvider={selectedProvider} hasError={!!stepError} />
      <ErrorLabel id={PROVIDER_SELECTION_ERROR_ID} hasError={!!stepError} focusOnError={true}>
        {stepError}
      </ErrorLabel>
    </VerificationStep>
  );
};

type VerificationResponse = NonNullable<BasketFields['verificationResponse']>;

const ContactInfo: React.FC<{ verificationResponse: VerificationResponse; verificationProvider: string }> = ({
  verificationResponse,
  verificationProvider,
}) => {
  const formData = useMemo(() => getFormFieldProps(verificationResponse.formValues), [verificationResponse.formValues]);
  const hasInternetProduct = useBasket((s) => s.hasInternetProduct)();

  // Form is validated and submitted from StepNavigation2 when user clicks next-button
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // TODO: validate data here (extract to form utils)
    const submittableData = Object.fromEntries(new FormData(e.currentTarget).entries()) as Record<CustomerInfoFields, string>;
    useBasket.setState({ contactInfo: submittableData });
  };

  return (
    <div className={styles.contactInfoWrapper}>
      <UserDataInformation providerName={verificationProvider} />
      <Card>
        <div className={styles.titleWrapper}>
          <h2 className="title-3">Din kontaktinformasjon</h2>
        </div>
        <p>Vennligst fyll inn skjemaet med din kontaktinformasjon.</p>
        <br />
        <form onSubmit={onSubmit} name="contactInfo">
          <div className={styles.fieldGrid}>
            <TextField {...formData.email} />
            {/* These are read-only if provided */}
            <TextField {...formData.mobilePhone} />
            <TextField {...formData.firstName} />
            <TextField {...formData.lastName} />
          </div>

          <hr />
          <div className={styles.fieldGrid}>
            <div>
              {/* B-Day read-only if provided */}
              <TextField {...formData.birthDate} />
              <br />
              <TextField {...formData.street} />
              <div className={styles.fieldGrid}>
                <TextField {...formData.zip} />
                <TextField {...formData.city} />
              </div>
            </div>
          </div>
        </form>
        <div className="information">
          <Icon type="informationCircleBold" className="icon" size={1} />
          <p>Du trenger ikke å spesifisere leilighetsnummer eller postboks i adressen</p>
        </div>
      </Card>
      {!hasInternetProduct && <LaterActivation />}
    </div>
  );
};

// TODO: get correct MAX-VALUE we should use from Haugvoll
const MAX_LATER_DAYS = 30;
const MIN_LATER_DAYS = 2;
const LaterActivation = () => {
  const useDelayedActivation = useBasket((s) => s.useDelayedActivation);
  const onCheckBoxChanged: React.ChangeEventHandler<HTMLInputElement> = ({ target: { checked } }) => {
    if (checked) {
      useBasket.setState({ useDelayedActivation: checked });
    } else {
      useBasket.setState({ useDelayedActivation: checked, delayedActivationDate: undefined });
    }
  };
  const onDateChanged: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    useBasket.setState({ delayedActivationDate: e.target.value });
    e.target.setCustomValidity('');
  };
  // Min Date for activation is set to the day after tomorrow, in order for CCB batches to work. Batches run by midnight and look for products that has tomorrow as activation date, i.e. setting today ir tomorrow will not work.
  const minDate = format(addDays(new Date(), MIN_LATER_DAYS), 'yyyy-MM-dd');
  const maxDate = format(addDays(new Date(), MAX_LATER_DAYS), 'yyyy-MM-dd');
  return (
    <Card>
      <H2 lookLike="title-3" className={styles.marginBottom8}>
        Ønsker du en senere aktivering av TV-pakken?
      </H2>
      <Body style={{ marginBottom: '1rem' }}>
        Det kan for eksempel være fordi du venter på at fiber skal installeres, eller ditt nåværende TV-abonnement skal avsluttes.{' '}
      </Body>

      <div className={styles.laterActivationFields}>
        <Checkbox
          data-testid="delayed-activation-checkbox"
          className={styles.checkbox}
          checked={useDelayedActivation}
          onChange={onCheckBoxChanged}
        >
          <Body>Jeg ønsker å vente med å aktivere TV-pakken</Body>
        </Checkbox>
        <div>
          <TextField
            className={styles.marginBottom8}
            data-testid="delayed-activation-date"
            disabled={!useDelayedActivation}
            onChange={onDateChanged}
            type="date"
            name="delayedActivationDate"
            label="Velg aktiveringsdato"
            min={minDate}
            max={maxDate}
          />
          <SubBody>
            Du kan velge en aktiveringsdato maksimalt {MAX_LATER_DAYS} dager frem i tid. Hvis du ikke velger en dato vil TV-pakken din bli
            aktivert umiddelbart eller så fort TV-utstyret ditt er blitt levert.
          </SubBody>
        </div>
      </div>
    </Card>
  );
};
